import React, { useState, useEffect } from 'react';
import { fabric } from 'fabric';

const ArtisticTextModal = ({ isOpen, onClose, onGenerate }) => {
    const [text, setText] = useState('Texto Artístico');
    const [fontFamily, setFontFamily] = useState('Arial');
    const [fontSize, setFontSize] = useState(60);
    const [fillType, setFillType] = useState('solid'); // solid, gradient, pattern
    const [fillColor, setFillColor] = useState('#3b82f6');
    const [gradientColor1, setGradientColor1] = useState('#3b82f6');
    const [gradientColor2, setGradientColor2] = useState('#8b5cf6');
    const [gradientDirection, setGradientDirection] = useState('horizontal');
    const [strokeColor, setStrokeColor] = useState('#1e40af');
    const [strokeWidth, setStrokeWidth] = useState(0);
    const [shadowColor, setShadowColor] = useState('#000000');
    const [shadowBlur, setShadowBlur] = useState(0);
    const [shadowOffsetX, setShadowOffsetX] = useState(5);
    const [shadowOffsetY, setShadowOffsetY] = useState(5);
    const [previewUrl, setPreviewUrl] = useState(null);

    const fonts = [
        'Arial', 'Times New Roman', 'Courier New', 'Verdana', 'Georgia',
        'Comic Sans MS', 'Impact', 'Trebuchet MS', 'Arial Black'
    ];

    // Preview canvas ref
    const canvasRef = React.useRef(null);
    const fabricCanvasRef = React.useRef(null);

    useEffect(() => {
        if (isOpen && canvasRef.current && !fabricCanvasRef.current) {
            fabricCanvasRef.current = new fabric.Canvas(canvasRef.current, {
                width: 400,
                height: 200,
                backgroundColor: '#f1f5f9'
            });
        }

        if (isOpen && fabricCanvasRef.current) {
            updatePreview();
        }

        return () => {
            if (!isOpen && fabricCanvasRef.current) {
                fabricCanvasRef.current.dispose();
                fabricCanvasRef.current = null;
            }
        };
    }, [isOpen, text, fontFamily, fontSize, fillType, fillColor, gradientColor1, gradientColor2, gradientDirection, strokeColor, strokeWidth, shadowColor, shadowBlur, shadowOffsetX, shadowOffsetY]);

    const updatePreview = () => {
        const canvas = fabricCanvasRef.current;
        if (!canvas) return;

        canvas.clear();
        canvas.setBackgroundColor('#f1f5f9', canvas.renderAll.bind(canvas));

        const textObj = new fabric.Text(text, {
            left: 200,
            top: 100,
            originX: 'center',
            originY: 'center',
            fontFamily: fontFamily,
            fontSize: fontSize,
            stroke: strokeColor,
            strokeWidth: strokeWidth,
            shadow: new fabric.Shadow({
                color: shadowColor,
                blur: shadowBlur,
                offsetX: shadowOffsetX,
                offsetY: shadowOffsetY
            })
        });

        if (fillType === 'gradient') {
            const gradient = new fabric.Gradient({
                type: 'linear',
                gradientUnits: 'percentage',
                coords: gradientDirection === 'horizontal' ? { x1: 0, y1: 0, x2: 1, y2: 0 } : { x1: 0, y1: 0, x2: 0, y2: 1 },
                colorStops: [
                    { offset: 0, color: gradientColor1 },
                    { offset: 1, color: gradientColor2 }
                ]
            });
            textObj.set('fill', gradient);
        } else {
            textObj.set('fill', fillColor);
        }

        canvas.add(textObj);
        canvas.renderAll();
    };

    const handleGenerate = () => {
        const config = {
            text,
            fontFamily,
            fontSize,
            fillType,
            fillColor,
            gradientColor1,
            gradientColor2,
            gradientDirection,
            strokeColor,
            strokeWidth,
            shadowColor,
            shadowBlur,
            shadowOffsetX,
            shadowOffsetY
        };
        onGenerate(config);
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center z-[9999] p-4 font-sans">
            <div className="bg-slate-900 rounded-2xl border border-slate-700 p-6 max-w-4xl w-full shadow-2xl max-h-[90vh] overflow-y-auto flex gap-6">

                {/* Controles */}
                <div className="flex-1 space-y-6 overflow-y-auto pr-2 custom-scrollbar">
                    <div className="flex items-center justify-between">
                        <h3 className="text-xl font-bold text-white flex items-center">
                            <span className="text-2xl mr-2">🎨</span> Texto Artístico
                        </h3>
                    </div>

                    {/* Texto y Fuente */}
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-slate-300 mb-1">Texto</label>
                            <input
                                type="text"
                                value={text}
                                onChange={(e) => setText(e.target.value)}
                                className="w-full bg-slate-800 border border-slate-600 rounded px-3 py-2 text-white focus:ring-2 focus:ring-blue-500 outline-none"
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-slate-300 mb-1">Fuente</label>
                                <select
                                    value={fontFamily}
                                    onChange={(e) => setFontFamily(e.target.value)}
                                    className="w-full bg-slate-800 border border-slate-600 rounded px-3 py-2 text-white focus:ring-2 focus:ring-blue-500 outline-none"
                                >
                                    {fonts.map(f => <option key={f} value={f}>{f}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-300 mb-1">Tamaño</label>
                                <input
                                    type="number"
                                    value={fontSize}
                                    onChange={(e) => setFontSize(parseInt(e.target.value))}
                                    className="w-full bg-slate-800 border border-slate-600 rounded px-3 py-2 text-white focus:ring-2 focus:ring-blue-500 outline-none"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Relleno */}
                    <div className="bg-slate-800/50 p-4 rounded-lg border border-slate-700">
                        <label className="block text-sm font-bold text-blue-400 mb-3 uppercase tracking-wider">Relleno</label>
                        <div className="flex space-x-4 mb-4">
                            <label className="flex items-center cursor-pointer">
                                <input type="radio" checked={fillType === 'solid'} onChange={() => setFillType('solid')} className="mr-2 text-blue-500" />
                                <span className="text-slate-300">Sólido</span>
                            </label>
                            <label className="flex items-center cursor-pointer">
                                <input type="radio" checked={fillType === 'gradient'} onChange={() => setFillType('gradient')} className="mr-2 text-blue-500" />
                                <span className="text-slate-300">Degradado</span>
                            </label>
                        </div>

                        {fillType === 'solid' ? (
                            <div>
                                <label className="block text-xs text-slate-400 mb-1">Color</label>
                                <div className="flex items-center space-x-2">
                                    <input type="color" value={fillColor} onChange={(e) => setFillColor(e.target.value)} className="h-8 w-12 rounded cursor-pointer bg-transparent border-0" />
                                    <input type="text" value={fillColor} onChange={(e) => setFillColor(e.target.value)} className="flex-1 bg-slate-700 border border-slate-600 rounded px-2 py-1 text-sm text-white" />
                                </div>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="block text-xs text-slate-400 mb-1">Color Inicio</label>
                                        <div className="flex items-center space-x-2">
                                            <input type="color" value={gradientColor1} onChange={(e) => setGradientColor1(e.target.value)} className="h-8 w-8 rounded cursor-pointer bg-transparent border-0" />
                                            <input type="text" value={gradientColor1} onChange={(e) => setGradientColor1(e.target.value)} className="w-full bg-slate-700 border border-slate-600 rounded px-2 py-1 text-xs text-white" />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-xs text-slate-400 mb-1">Color Fin</label>
                                        <div className="flex items-center space-x-2">
                                            <input type="color" value={gradientColor2} onChange={(e) => setGradientColor2(e.target.value)} className="h-8 w-8 rounded cursor-pointer bg-transparent border-0" />
                                            <input type="text" value={gradientColor2} onChange={(e) => setGradientColor2(e.target.value)} className="w-full bg-slate-700 border border-slate-600 rounded px-2 py-1 text-xs text-white" />
                                        </div>
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-xs text-slate-400 mb-1">Dirección</label>
                                    <select value={gradientDirection} onChange={(e) => setGradientDirection(e.target.value)} className="w-full bg-slate-700 border border-slate-600 rounded px-2 py-1 text-sm text-white">
                                        <option value="horizontal">Horizontal</option>
                                        <option value="vertical">Vertical</option>
                                    </select>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Borde y Sombra */}
                    <div className="grid grid-cols-2 gap-4">
                        <div className="bg-slate-800/50 p-4 rounded-lg border border-slate-700">
                            <label className="block text-sm font-bold text-blue-400 mb-3 uppercase tracking-wider">Borde</label>
                            <div className="space-y-3">
                                <div>
                                    <label className="block text-xs text-slate-400 mb-1">Grosor</label>
                                    <input type="range" min="0" max="10" value={strokeWidth} onChange={(e) => setStrokeWidth(parseInt(e.target.value))} className="w-full" />
                                </div>
                                <div>
                                    <label className="block text-xs text-slate-400 mb-1">Color</label>
                                    <div className="flex items-center space-x-2">
                                        <input type="color" value={strokeColor} onChange={(e) => setStrokeColor(e.target.value)} className="h-6 w-8 rounded cursor-pointer bg-transparent border-0" />
                                        <span className="text-xs text-slate-300">{strokeColor}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="bg-slate-800/50 p-4 rounded-lg border border-slate-700">
                            <label className="block text-sm font-bold text-blue-400 mb-3 uppercase tracking-wider">Sombra</label>
                            <div className="space-y-3">
                                <div>
                                    <label className="block text-xs text-slate-400 mb-1">Desenfoque</label>
                                    <input type="range" min="0" max="50" value={shadowBlur} onChange={(e) => setShadowBlur(parseInt(e.target.value))} className="w-full" />
                                </div>
                                <div className="grid grid-cols-2 gap-2">
                                    <div>
                                        <label className="block text-xs text-slate-400 mb-1">X</label>
                                        <input type="number" value={shadowOffsetX} onChange={(e) => setShadowOffsetX(parseInt(e.target.value))} className="w-full bg-slate-700 border border-slate-600 rounded px-1 py-1 text-xs text-white" />
                                    </div>
                                    <div>
                                        <label className="block text-xs text-slate-400 mb-1">Y</label>
                                        <input type="number" value={shadowOffsetY} onChange={(e) => setShadowOffsetY(parseInt(e.target.value))} className="w-full bg-slate-700 border border-slate-600 rounded px-1 py-1 text-xs text-white" />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-xs text-slate-400 mb-1">Color</label>
                                    <div className="flex items-center space-x-2">
                                        <input type="color" value={shadowColor} onChange={(e) => setShadowColor(e.target.value)} className="h-6 w-8 rounded cursor-pointer bg-transparent border-0" />
                                        <span className="text-xs text-slate-300">{shadowColor}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Preview y Acciones */}
                <div className="w-1/3 flex flex-col">
                    <div className="bg-slate-800 rounded-lg border border-slate-700 p-4 flex-1 flex flex-col">
                        <h4 className="text-white font-medium mb-4">Vista Previa</h4>
                        <div className="flex-1 bg-slate-100 rounded-lg overflow-hidden flex items-center justify-center border border-slate-600 bg-checkerboard">
                            <canvas ref={canvasRef} />
                        </div>
                    </div>

                    <div className="mt-6 flex flex-col space-y-3">
                        <button
                            onClick={handleGenerate}
                            className="w-full py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:shadow-lg hover:shadow-blue-900/50 transition-all font-bold text-lg"
                        >
                            ✨ Insertar Texto
                        </button>
                        <button onClick={onClose} className="w-full py-3 text-slate-400 hover:text-white border border-slate-700 hover:bg-slate-800 rounded-xl transition-colors">
                            Cancelar
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ArtisticTextModal;
